import { Component, ReactNode } from 'react'
import * as cn from 'classnames'
import * as identity from 'lodash/identity'
import * as isEqual from 'lodash/isEqual'
import * as isNumber from 'lodash/isNumber'
import * as styles from 'styles/molecules/tables/Table.css'
import { Editable, FooterDefinition, SortingDirection, SortingProp, SortingState } from 'interfaces/table'
import { TooltipIcon } from 'components/features/pfm/Tooltip/TooltipIcon'
import Paginator, { PaginatorStateProps } from 'components/molecules/tables/Paginator'
import PageSizeSelector, { PageSizeSelectorStateProps } from 'components/atoms/tables/PageSizeSelector'
import TableFooter, { TableFooterType } from 'components/molecules/tables/TableFooter'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { getRowProps, isEmptyFormRow, isFormRow } from 'helpers/tableHelpers'

export type SameSortingSelector = 'same'
export type SortingSelector<TProps> = (props: TProps) => SortablePrimitive | null | undefined
export type SortablePrimitive = string | number

export const SAME_SORTING_SELECTOR: SameSortingSelector = 'same'

type TableColumnDefinitionBase<TProps, TSelectedProps> = {
  name: string
  title: ReactNode
  helpText?: string
  component?: (props: TSelectedProps) => ReactNode | string
  sortingSelector?: SortingSelector<TProps> | SameSortingSelector
  collapseSame?: boolean | ((props: TProps) => SortablePrimitive)
  responsiveTitle?: boolean
  // use percents here
  width?: string
  getColSpan?: (props: TSelectedProps) => number
}

export type TableColumnDefinition<TProps, TSelectedProps> = TableColumnDefinitionBase<TProps, TSelectedProps> & {
  propsSelector?: (props: TProps) => TSelectedProps
}

export type TableColumnInnerDefinition<TProps, TSelectedProps> = TableColumnDefinitionBase<TProps, TSelectedProps> & {
  propsSelector: (props: TProps) => TSelectedProps
}

export type TableDispatchProps = {
  changeSorting?: (sorting: SortingState) => void
  onPageClick?: (page: number) => void
  onPageSizeClick?: (page: number) => void
}

export type Table2StateProps<TProps> = {
  className?: string
  rows: TProps[]
  isLoading?: boolean
}

export type TableStateProps<TProps> = {
  className?: string
  columns: TableColumnDefinition<TProps, any>[]
  rows: TProps[]
  footer?: FooterDefinition<TProps>[]
  sorting?: SortingProp
  isHeaderWhite?: boolean
  isLoading?: boolean
  isMobile?: boolean
  pageSize?: PageSizeSelectorStateProps
  paginator?: PaginatorStateProps
}

export type TableState<TProps> = {
  that: Table<TProps>
  rows: TProps[]
  rowSpans: number[][]
  sorting?: SortingState
}

export type TableProps<TProps> =
  & TableStateProps<TProps>
  & TableDispatchProps

export type TableComponentType<TRow> = {
  new (): Table<TRow>
  compare: any
  getDerivedStateFromProps?: any
}

function isSortingWithColumnIndex (sorting: SortingState | SortingProp): sorting is SortingState {
  return isNumber(sorting.columnIndex)
}

class Table<TProps> extends Component<TableProps<TProps>, TableState<TProps>> {
  /**
 * For now only `number` and `string` types are supported as an output of `sortSelector`.
 * It's used inside the `Array.prototype.sort` function.
 */
  static compare<TProps> (
    direction: SortingDirection,
    selector: SortingSelector<TProps>,
    rawA: Editable<TProps>,
    rawB: Editable<TProps>
  ): number {

    const aIsEmptyForm = isEmptyFormRow(rawA)
    const bIsEmptyForm = isEmptyFormRow(rawB)
    if (aIsEmptyForm || bIsEmptyForm) {
      // the blank form always should be the first row
      return aIsEmptyForm ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY
    }

    const aIsForm = isFormRow(rawA)
    const bIsForm = isFormRow(rawB)
    if (aIsForm || bIsForm) {
      // if `a` is a form of props and `b` is a `props` itself they should be nearby
      // that is why I'm operating with `1`
      if (aIsForm && !bIsForm && getRowProps(rawA) === rawB) {
        return 1
      }

      if (bIsForm && !aIsForm && getRowProps(rawB) === rawA) {
        return -1
      }
    }

    // as TProps because it's not form row
    // passing form rows here prevented by upper code
    const a = selector(getRowProps(rawA) as TProps)
    const b = selector(getRowProps(rawB) as TProps)

    let comparison = 0

    // `undefined`s and `null`s should be the first rows in asc
    if (a === undefined || b === undefined) {
      comparison = a === b ? 0 : (a === undefined ? -2000 : 2000)
    } else if (a === null || b === null) {
      comparison = a === b ? 0 : (a === null ? -1000 : 1000)
    }
    if (typeof a === 'string' && typeof b === 'string') {
      comparison = a.localeCompare(b)
    }
    if (typeof a === 'number' && typeof b === 'number') {
      comparison = a < b ? -1000 : (a > b ? 1000 : 0)
    }

    return comparison * (direction === 'asc' ? 1 : -1)
  }

  static getDerivedStateFromProps (nextProps: TableProps<any>, prevState: TableState<any>) {
    const { that } = prevState
    that.normalizeColumns(nextProps.columns)
    const normalizedSorting = that.getNormalizedSortingProp(nextProps) || undefined

    /**
     * The table is server-side if `changeSorting` is defined so we take `nextProps.sorting` for granted.
     * Otherwise `state.sorting` is more important.
     */
    const updatedState = nextProps.changeSorting
      ? { ...prevState, sorting: normalizedSorting }
      : prevState

    const state = that.buildState(nextProps, updatedState)
    return state
  }

  private columns: TableColumnInnerDefinition<TProps, any>[] | null = null

  constructor (props: TableProps<TProps>) {
    super(props)
    this.normalizeColumns(props.columns)
    this.state = this.buildState(props)
  }

  render () {
    const { rows } = this.state
    const { className, footer } = this.props

    const TypedTableFooter: TableFooterType<TProps> = TableFooter

    const tableInnerClass = cn(styles.tableInner, {
      [className || '']: typeof className !== 'undefined',
    })

    return !!rows.length ? (
      <div className={ styles.table }>
        <table className={ tableInnerClass } data-qa-type='table'>
          { this.renderHeader() }
          { this.renderRows() }
          { footer && <TypedTableFooter rows={ rows } definition={ footer } /> }
        </table>
        { this.renderPaginator() }
        { this.renderLoadingOverlay() }
      </div>
    ) : null
  }

  private renderHeader = () => {
    const { isHeaderWhite, changeSorting, rows } = this.props

    const firstRow = rows[0] as Editable<any>
    const onlyOneEditableRow = rows.length === 1 && isFormRow(firstRow)

    return this.columns && this.columns.length && !onlyOneEditableRow ? (
      <thead data-qa-marker='table-head'>
        <tr
          className={ cn(styles.headerRow, { [styles.white]: Boolean(isHeaderWhite) }) }
          data-qa-type='table-row'
        >
          { this.columns.map((column, columnIndex) => {
            const { title, helpText, sortingSelector, width } = column
            const { sorting } = this.state

            const isSortingEnabled = Boolean(
              changeSorting ||  // server side
              sortingSelector   // client side
            )

            const isSorting = isSortingEnabled && sorting && sorting.columnIndex === columnIndex

            const cellClass = cn(styles.headerCell, {
              [styles.pointing]: isSortingEnabled,
            })

            const hintIcon = helpText ? (
              <div className={ styles.tooltipIconWrapper }>
                <TooltipIcon icon='question' wide>{ helpText }</TooltipIcon>
              </div>
            ) : null

            let sortingWrapper = null
            if (isSortingEnabled) {
              const sortingWrapperClass = cn(styles.sortingWrapper, {
                [styles.up]: isSorting && sorting && sorting.direction === 'asc',
                [styles.down]: isSorting && sorting && sorting.direction === 'desc',
                [styles.withHelpText]: !!helpText,
              })
              const sortingWrapperType = isSorting && sorting
                ? sorting.direction
                : ''
              sortingWrapper = (
                <div
                  className={ sortingWrapperClass }
                  data-qa-marker='sorting'
                  data-qa-type={ sortingWrapperType }
                />
              )
            }

            const cellStyle = width ? { width } : undefined

            return (
              <th
                key={ columnIndex }
                className={ cellClass }
                style={ cellStyle }
                onClick={ () => this.onHeaderClick(columnIndex) }
                data-qa-marker={ column.name }
              >
                <span className={ styles.headerTitle }>{ title }{ hintIcon }</span>
                { sortingWrapper }
              </th>
            )
          })}
        </tr>
      </thead>
    ) : null
  }


  private renderRows = () => {
    const { rows } = this.state

    return this.columns && this.columns.length ? (
      <tbody
        className={ styles.body }
        data-qa-marker='table-body'
      >
        { rows.map(this.renderRow) }
      </tbody>
    ) : null
  }

  private renderRow = (props: TProps, rowIndex: number) => {
    const { rowSpans } = this.state

    return (
      <tr key={ rowIndex } className={ styles.row } data-qa-type='table-row'>
        { (this.columns || []).map((column: TableColumnDefinition<TProps, any>, columnIndex: number) => {
          const { propsSelector, component, getColSpan, title, responsiveTitle, helpText } = column

          const identityHelper = (value: TProps) => value
          const fixedComponent = component || identityHelper
          const fixedPropsSelector = propsSelector || identityHelper

          const rowSpan = (rowSpans[columnIndex] || [])[rowIndex]
          const selectedProps = fixedPropsSelector(props)
          const colSpan = getColSpan ? getColSpan(selectedProps) : undefined
          const { collapseSame } = column
          if (collapseSame && rowSpan === -1 || colSpan === 0) {
            return (
              <td
                key={ columnIndex }
                style={{ display: 'none' }}
                data-qa-type='table-cell'
                data-qa-marker={ column.name }
              />
            )
          }

          const hintIcon = helpText ? (
            <div className={ styles.tooltipIconWrapper }>
              <TooltipIcon icon='question' wide>{ helpText }</TooltipIcon>
            </div>
          ) : null

          const titleSection = responsiveTitle ? (
            <h3 className={ styles.responsiveTitle }>
              <span className={ styles.responsiveTitleText }>
                { title }{ hintIcon }
              </span>
            </h3>
          ) : null

          return (
            <td
              data-qa-type='table-cell'
              data-qa-marker={ column.name }
              key={ columnIndex }
              className={ styles.cell }
              colSpan={ colSpan }
              rowSpan={ collapseSame && rowSpan > 1 ? rowSpan : undefined }
            >
              { titleSection }
              { fixedComponent(selectedProps) }
            </td>
          )
        })}
      </tr>
    )
  }

  private renderPaginator () {
    const { paginator, onPageClick, onPageSizeClick, pageSize } = this.props

    if (!paginator || !pageSize || !onPageClick || !onPageSizeClick) {
      return null
    }

    return (
      <div className={ styles.paginatorWrapper } data-qa-marker='table-paginator'>
        { Boolean(paginator) && (
          <Paginator { ...paginator } onPageChange={ onPageClick } />
        ) }
        { Boolean(pageSize) && (
          <div className={ styles.pageSizeSelectorWrapper }>
            <PageSizeSelector { ...pageSize } onPageSizeChange={ onPageSizeClick } />
          </div>
        ) }
      </div>
    )
  }

  private renderLoadingOverlay () {
    return this.props.isLoading ? <LoadingOverlay /> : null
  }

  private normalizeColumns (columns: TableColumnDefinition<TProps, any>[]) {
    this.columns = columns.map((column) => ({
      ...column,
      propsSelector: column.propsSelector || identity,
    }))
  }

  private getNormalizedSortingProp (props: TableProps<TProps>): SortingState | null {
    const { sorting } = props

    if (!sorting || !this.columns) {
      return null
    }

    if (isSortingWithColumnIndex(sorting)) {
      return sorting
    }

    const sortingWithIndex: SortingState = {
      ...sorting,
      columnIndex: this.columns.findIndex(({ name }) => name === sorting.columnName),
    }

    return sortingWithIndex
  }

  private getSortedRows (props: TableProps<TProps>, sorting?: SortingState): TProps[] {
    const { rows, changeSorting } = props

    if (!sorting || changeSorting || !this.columns) {
      return rows
    }

    const { sortingSelector, propsSelector } = this.columns[sorting.columnIndex]
    if (!sortingSelector) {
      return rows
    }

    const columnSortingSelector = sortingSelector === 'same' ? propsSelector : sortingSelector
    // TODO get rid off ! in "row.__props!"
    const editableSortingSelector = (row: Editable<TProps>): SortablePrimitive =>
      isFormRow(row) ? columnSortingSelector(row.__props!) : columnSortingSelector(row)

    const sortedRows = [...rows].sort((a: TProps, b: TProps): number =>
      Table.compare(sorting.direction, editableSortingSelector, a, b))

    return sortedRows
  }

  private calculateRowSpans ({ rows, sorting }: Pick<TableProps<TProps>, 'rows' | 'sorting'>): number[][] {
    if (!rows || !rows.length || !this.columns) {
      return []
    }

    const rowSpans = Array(this.columns.length)
    this.columns.forEach((column, columnIndex) => {
      const { collapseSame, propsSelector } = column

      if (!collapseSame) {
        return
      }

      // prevent collapsing if it's not the same column which is used for sorting
      if (sorting && sorting.columnIndex !== columnIndex) {
        return
      }

      const selector = typeof collapseSame === 'function' ?
        collapseSame :
        propsSelector

      /**
       * I need to calculate raw spans for cells if actual props in adjacent cells are the same
       * and `collapseSame` option is passed for a column.
       *
       * I traverse through the rows in a reverse order and count the same cells
       * then dump this value to an array stored in props.
       *
       * The array is unique for every column.
       *
       * `-1` means the specific cell shouldn't be printed
       * `1` means a common row span
       * Other values mean specific row span value
       */
      let rowSpanCount = 1
      let rowValue: any = selector!(rows[rows.length - 1])
      let i = rows.length
      const spanArray = Array(i)
      while (i--) {
        const prevValue = i === 0 ? null : selector!(rows[i - 1])
        if (isEqual(rowValue, prevValue)) {
          rowSpanCount++
          spanArray[i] = -1
        } else {
          rowValue = prevValue
          spanArray[i] = rowSpanCount
          rowSpanCount = 1
        }
      }
      rowSpans[columnIndex] = spanArray
    })

    return rowSpans
  }

  private resolveSorting = (props: TableProps<TProps>, state?: Pick<TableState<TProps>, 'sorting'>): SortingState | null => {
    const { sorting: propsSorting } = props
    if (!propsSorting && !(state && state.sorting)) {
      return null
    }

    return state && state.sorting
      ? state.sorting
      : this.getNormalizedSortingProp(props)
  }

  private buildState = (props: TableProps<TProps>, state?: Pick<TableState<TProps>, 'sorting'>): TableState<TProps> => {
    const sorting = this.resolveSorting(props, state) || undefined
    const sortedRows = this.getSortedRows(props, sorting)

    const rowSpans = this.calculateRowSpans({ rows: sortedRows, sorting })

    return { rows: sortedRows, rowSpans, sorting, that: this }
  }

  private onHeaderClick = (clickedColumnIndex: number) => {
    const { sorting: propsSorting, changeSorting } = this.props
    let stateSorting: SortingState

    // server sorting
    if (changeSorting && this.columns) {
      const direction = propsSorting && propsSorting.columnIndex === clickedColumnIndex
        ? (propsSorting.direction === 'desc' ? 'asc' : 'desc')
        : 'asc'

      stateSorting = {
        direction,
        columnIndex: clickedColumnIndex,
        columnName: this.columns[clickedColumnIndex].name,
      }

      changeSorting(stateSorting)
    } else {
      // client sorting
      const sortingSelector = this.columns ? this.columns[clickedColumnIndex].sortingSelector : undefined
      if (!sortingSelector) {
        return
      }

      stateSorting = { ...this.state.sorting } as SortingState
      if (!stateSorting || stateSorting.columnIndex !== clickedColumnIndex) {
        stateSorting = {
          columnIndex: clickedColumnIndex,
          direction: 'asc',
        } as SortingState
      } else {
        stateSorting.direction = stateSorting.direction === 'asc' ? 'desc' : 'asc'
      }
    }

    // this `setState` is needed to eliminate a gap between a user's click and sorting direction change
    this.setState(this.buildState(this.props, { sorting: stateSorting }))
  }
}

export default Table
